import { Injectable } from "@angular/core";

import { first } from "rxjs/operators";

import { LoginService } from "@e-forsyning/common/auth";
import { AktuelBrugerService, Bruger } from "@e-forsyning/common/bruger";
import { Installation, InstallationKey, InstallationService, VaelgInstallationService } from "@e-forsyning/common/installation";
import { NoteRepository } from "@e-forsyning/common/note";
import { RedirectService } from "@e-forsyning/common/router";

@Injectable({ providedIn: "root" })
export class DeeplinkSandbox {
    constructor(
        private readonly loginService: LoginService,
        private readonly aktuelBrugerService: AktuelBrugerService,
        private readonly redirectService: RedirectService,
        private readonly installationService: InstallationService,
        private readonly vaelgInstallationService: VaelgInstallationService,
        private readonly noteRepository: NoteRepository
    ) {}

    public async loginMedGemteCredentials(): Promise<boolean> {
        const bruger = await this.hentAktuelleBruger();
        return !!bruger || (await this.tryLoginMedGemteCredentials());
    }

    public async findInstallationForNoteId(noteId: number): Promise<Installation> {
        const installationKey = await this.hentInstallationKeyForNoteId(noteId);
        const installationer = await this.findInstallationerForEjendom(installationKey.ejendomNr);
        return this.findInstallation(installationer, installationKey.installationNr);
    }

    public async findInstallationMedNummer(ejendomNr: number, installationNr: number): Promise<Installation> {
        const installationer = await this.findInstallationerForEjendom(ejendomNr);
        return this.findInstallation(installationer, installationNr);
    }

    public findInstallationerForEjendom(ejendomNr: number): Promise<Installation[]> {
        return this.installationService
            .hentInstallationerForEjendom(ejendomNr)
            .pipe(first())
            .toPromise();
    }

    public findInstallation(installationer: Installation[], installationNr: number): Installation {
        const result =
            (installationNr && installationer.find(i => i.installationNr === installationNr)) ||
            (!installationNr && installationer.length === 1 && installationer[0]);
        return result;
    }

    public vaelgInstallation(installation: Installation): void {
        this.vaelgInstallationService.vaelgInstallation(installation, false);
    }

    public redirectTilLogin(): void {
        // Vi benytter setTimeout, da routeren ikke tillader at redirecte på det tidspunkt vi har behov for.
        setTimeout(() => this.redirectService.redirectTilLogin(true));
    }

    public redirectTilNoter(noteId: number): void {
        setTimeout(() => this.redirectService.redirect(`noter/${noteId}`));
    }

    public redirectTilSide(side: string): void {
        setTimeout(() => this.redirectService.redirect(side));
    }

    public redirectTilInstallationer(): void {
        setTimeout(() => this.redirectService.redirectTilInstallationer());
    }

    private hentInstallationKeyForNoteId(noteId: number): Promise<InstallationKey> {
        return this.noteRepository
            .hentInstallationKeyForNoteId(noteId)
            .pipe(first())
            .toPromise();
    }

    private async hentAktuelleBruger(): Promise<Bruger> {
        return this.aktuelBrugerService
            .select()
            .pipe(first())
            .toPromise();
    }

    private async tryLoginMedGemteCredentials(): Promise<boolean> {
        try {
            await this.loginService
                .loginMedGemteCredentials()
                .pipe(first())
                .toPromise();
            return true;
        } catch {
            return false;
        }
    }
}
